import React, { useState } from 'react';

import {
    Sync,
    SafetyCheck,
} from '@mui/icons-material';

import Container from '../../components/blocks/container';
import CommonButton from '../../components/buttons/common-button';
import InfoBox from '../../components/blocks/messages/info-box';

import SyncService from '../../services/SyncService';

/**
 * AdminSyncWinsavPage Component
 * This component is a placeholder for the Winsav synchronization page.
 * It currently displays a message indicating that the page is under development.
 * 
 * @returns {JSX.Element} The rendered component.
 */
const AdminSyncWinsavPage = () => {

    // State to manage the message displayed on the page
    const [syncStatusMessage, setSyncStatusMessage] = useState(null);
    const [syncNewDataMessage, setSyncNewDataMessage] = useState(null);
    const [syncExistingDataMessage, setSyncExistingDataMessage] = useState(null);

    /**
     * Handles the click event for checking the synchronization status.
     * It calls the SyncService to check the status and updates the state with the response message
     * @param {Event} event - The click event.
     * @returns {Promise<void>} A promise that resolves when the status is checked.
     */
    const handleCheckSyncStatus = async () => {
        try {
            // Simulate checking sync status
            const response = await SyncService.checkSyncStatus();
            if ( response.is_syncing === false ) {
                setSyncStatusMessage({type: 'info', text: 'Aucune synchronisation en cours.'});
            } else {
                setSyncStatusMessage({type: 'warning', text: 'Une synchronisation est en cours.'});
            }
        } catch (error) {
            setSyncStatusMessage({type: 'error', text: error.message || 'Echec de la vérification de l\'état de synchronisation.'});
        }
    };

    /**
     * Handles the click event for syncing new data.
     * It calls the SyncService to sync new data and updates the state with the response message
     * @param {Event} event - The click event.
     * @returns {Promise<void>} A promise that resolves when the new data is synced.
     */
    const handleSyncNewData = async () => {
        try {
            // Simulate syncing new data
            const response = await SyncService.syncNewData();
            setSyncNewDataMessage({type: 'info', text: response.message || 'La synchronisation des nouvelles données est en cours. Veuillez patienter.'});
        } catch (error) {
            setSyncNewDataMessage({type: 'error', text: error.message || 'Echec de la synchronisation des nouvelles données.'});
        }
    };

    /**
     * Handles the click event for syncing existing data.
     * It calls the SyncService to sync existing data and updates the state with the response message
     * @param {Event} event - The click event.
     * @returns {Promise<void>} A promise that resolves when the existing data is synced.
     */
    const handleSyncExistingData = async () => {
        try {
            // Simulate syncing existing data
            const response = await SyncService.syncExistingData();
            setSyncExistingDataMessage({type: 'info', text: response.message || 'La synchronisation des données existantes est en cours. Veuillez patienter.'});
        } catch (error) {
            setSyncExistingDataMessage({type: 'error', text: error.message || 'Echec de la synchronisation des données existantes.'});
        }
    };

    return (
        <Container
            className="admin-sync-winsav-page"
        >
            <h1>Synchronisation Winsav</h1>

            <h2>Vérification de l'état de synchronisation</h2>

            { syncStatusMessage && (
                <InfoBox
                    text={syncStatusMessage.text}
                    type={syncStatusMessage.type}
                    className="sync-status-message"
                    disabledCloseBtn={true}
                />
            )}

            <CommonButton
                label="Vérifier l'état de synchronisation"
                icon={<SafetyCheck />}
                className="sync-winsav-btn"
                onClick={() => handleCheckSyncStatus()}
                aria-label="Vérifier l'état de synchronisation de Winsav"
            >
                Synchroniser Winsav
            </CommonButton>
            
            <h2>Synchroniser les nouvelles données de Winsav</h2>

            { syncNewDataMessage && (
                <InfoBox
                    text={syncNewDataMessage.text}
                    type={syncNewDataMessage.type}
                    className="sync-status-message"
                    disabledCloseBtn={true}
                />
            )}

            <CommonButton
                label="Synchroniser les nouvelles données"
                icon={<Sync />}
                className="sync-winsav-btn"
                onClick={() => handleSyncNewData()}
                aria-label="Synchroniser Winsav"
            >
                Synchroniser Winsav
            </CommonButton>

            <h2>Synchroniser les données existantes de Winsav</h2>

            { syncExistingDataMessage && (
                <InfoBox
                    text={syncExistingDataMessage.text}
                    type={syncExistingDataMessage.type}
                    className="sync-status-message"
                    disabledCloseBtn={true}
                />
            )}

            <CommonButton
                label="Synchroniser les données existantes"
                icon={<Sync />}
                className="sync-winsav-btn"
                onClick={() => handleSyncExistingData()}
                aria-label="Synchroniser les données existantes de Winsav"
            >
                Synchroniser les données existantes
            </CommonButton>
        </Container>
    );
};

export default AdminSyncWinsavPage;
